import React, { useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import UnfoldMoreRoundedIcon from "@mui/icons-material/UnfoldMoreRounded";
import { toast } from "react-toastify";
import SpaceDashboardOutlinedIcon from "@mui/icons-material/SpaceDashboardOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { ReactComponent as TrustChatLogo } from "../../assets/images/Trustchat1.svg";
import { PlatformAdminProvider } from "./PlatformAdminContext";
import { getPlatformSession, signOutPlatformAdmin } from "./platformAuth";
import { menuPaperSx } from "./platformUi";
import "./platformAdmin.css";

const HEADER_META = {
  "/platform-admin/overview": { title: "Overview", subtitle: "Platform totals and recently registered businesses" },
  "/platform-admin/businesses": { title: "Businesses", subtitle: "Search and manage every workspace on TrustChat" },
};

function PlatformSidebar() {
  const navigate = useNavigate();
  const [profileAnchor, setProfileAnchor] = useState(null);
  const staff = getPlatformSession()?.staff;

  const items = [
    { to: "/platform-admin/overview", label: "Overview", Icon: SpaceDashboardOutlinedIcon },
    { to: "/platform-admin/businesses", label: "Businesses", Icon: StorefrontOutlinedIcon },
  ];

  return (
    <aside className="platform-admin-sidebar">
      <div className="platform-admin-sidebar-logo" onClick={() => navigate("/platform-admin/overview")}>
        <TrustChatLogo />
        <span className="platform-admin-sidebar-badge">Platform</span>
      </div>
      <div className="platform-admin-sidebar-section-label">Admin</div>
      <nav className="platform-admin-sidebar-nav">
        {items.map(({ to, label, Icon }) => (
          <NavLink key={to} to={to} className={({ isActive }) => `platform-admin-nav-item${isActive ? " active" : ""}`}>
            <Icon />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="platform-admin-sidebar-footer" onClick={(e) => setProfileAnchor(e.currentTarget)}>
        <Avatar sx={{ width: 32, height: 32, fontSize: 14, bgcolor: "#148DFD" }}>
          {(staff?.name || "?")[0].toUpperCase()}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ color: "#061522", fontFamily: "Inter", fontSize: 14, fontWeight: 500, lineHeight: "18px" }}>
            {staff?.name}
          </Typography>
          <Typography noWrap sx={{ color: "#93A1B0", fontFamily: "Inter", fontSize: 12, lineHeight: "16px" }}>
            {staff?.role}
          </Typography>
        </Box>
        <UnfoldMoreRoundedIcon sx={{ fontSize: 18, color: "#93A1B0" }} />
      </div>
      <Menu
        anchorEl={profileAnchor}
        open={Boolean(profileAnchor)}
        onClose={() => setProfileAnchor(null)}
        elevation={0}
        anchorOrigin={{ horizontal: "left", vertical: "top" }}
        transformOrigin={{ horizontal: "left", vertical: "bottom" }}
        MenuListProps={{ disablePadding: true }}
        PaperProps={{ sx: { ...menuPaperSx, width: 232, mt: "-8px" } }}
      >
        <Box sx={{ px: 2, pb: 1, mb: 0.5, borderBottom: "1px solid #EEF2F6" }}>
          <Typography noWrap sx={{ color: "#93A1B0", fontFamily: "Inter", fontSize: 12 }}>
            {staff?.email}
          </Typography>
        </Box>
        <MenuItem
          onClick={() => {
            signOutPlatformAdmin();
            toast.success("Signed out");
            navigate("/platform-admin/login", { replace: true });
          }}
          sx={{ gap: 1, color: "#FF4D4F !important" }}
        >
          <LogoutRoundedIcon sx={{ fontSize: 18 }} />
          Sign out
        </MenuItem>
      </Menu>
    </aside>
  );
}

function PlatformHeader() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isBusinessDetail = /^\/platform-admin\/businesses\/[^/]+$/.test(pathname);
  const meta = useMemo(() => HEADER_META[pathname], [pathname]);

  return (
    <header className="platform-admin-header">
      {isBusinessDetail ? (
        <Box
          component="button"
          type="button"
          onClick={() => navigate("/platform-admin/businesses")}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            border: "none",
            background: "transparent",
            cursor: "pointer",
            p: 0,
            color: "#405261",
            fontFamily: "Inter",
            fontSize: 16,
            lineHeight: "18px",
          }}
        >
          <ArrowBackRoundedIcon sx={{ fontSize: 20 }} />
          Back to Businesses
        </Box>
      ) : (
        <div>
          <h1 className="platform-admin-header-title">{meta?.title || "Platform Admin"}</h1>
          {meta?.subtitle ? <p className="platform-admin-header-subtitle">{meta.subtitle}</p> : null}
        </div>
      )}
    </header>
  );
}

export default function PlatformAdminLayout() {
  return (
    <PlatformAdminProvider>
      <div className="platform-admin-shell">
        <PlatformSidebar />
        <main className="platform-admin-main">
          <PlatformHeader />
          <div className="platform-admin-content">
            <Outlet />
          </div>
        </main>
      </div>
    </PlatformAdminProvider>
  );
}
